import React, { useEffect } from 'react';
import { Box, useDisclosure } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useOuterClick } from '@shared/utils/hooks/useOuterClick';
import { BurgerIconWrapper, PlainButtonWrapper } from './NavDrawerTrigger.styles';
import type { NavDrawerTriggerProps } from './NavDrawer.types';

/**
 * The hamburger button and the open state behind it. It owns `isOpen` and hands the drawer down
 * through `renderDrawer`, so the drawer itself can be a lazy chunk that only loads on first open.
 */
export const NavDrawerTrigger: React.FC<NavDrawerTriggerProps> = ({ drawerAnchor, onRegisterClose, renderDrawer, children }) => {
  const { tq } = useTranslation('adminDashboardV3');
  const { isOpen, onToggle, onClose } = useDisclosure();
  const menuRef = useOuterClick(onToggle, isOpen);

  // This component owns the open state, so it's the only place that can hand an embed host a way
  // to close the drawer. Registration rather than a prop: the host closes imperatively, in response
  // to an event of its own, and a controlled `isOpen` would mean rewriting every in-app call site.
  useEffect(() => {
    onRegisterClose?.(onClose);
  }, [onRegisterClose, onClose]);

  const isRenderProp = typeof children === 'function';
  const renderedChildren = isRenderProp ? children({ isOpen }) : children;

  return (
    <Box position="relative">
      {isRenderProp ? (
        <PlainButtonWrapper
          aria-label={tq`Menu`}
          onClick={() => onToggle()}
        >
          {renderedChildren}
        </PlainButtonWrapper>
      ) : (
        <BurgerIconWrapper
          aria-label={tq`Menu`}
          isActive={isOpen}
          onClick={() => onToggle()}
        >
          {renderedChildren}
        </BurgerIconWrapper>
      )}
      {renderDrawer({ isOpen, onClose, menuRef, drawerAnchor })}
    </Box>
  );
};
